import { request } from "$/lib/api.ts"
import {
  AchievementPeriod,
  AchievementReview,
  AchievementsResponse,
  AddonCardStats,
  AddonSource,
  AllStatsResponse,
  AllTrendsResponse,
  BikeStoreCapacityResponse,
  InspectionSimulationOverrides,
  InspectionSimulationResponse,
  LeadTimeStandings,
  MechanicAchievementsResponse,
  OverviewLateCounts,
  PriceSimulationOverrides,
  PriceSimulationResponse,
  Route,
  TimeUnit,
  populateRoute,
} from "@repo/common"
import { keepPreviousData, queryOptions } from "@tanstack/react-query"

export type LabelFilterMode = "include" | "exclude"

export interface StatsFilterArgs {
  labelIds: string[]
  labelFilterMode: LabelFilterMode
  includeZeroValueJobs: boolean
}

const buildStatsFilterParams = (
  args: StatsFilterArgs,
): Record<string, string> => {
  return {
    ...(args.labelIds.length > 0 && {
      labelIds: args.labelIds.join(","),
      labelFilterMode: args.labelFilterMode,
    }),
    ...(args.includeZeroValueJobs && { includeZeroValueJobs: "true" }),
  }
}

const statsFilterKeyFragment = (
  args: StatsFilterArgs,
): readonly [string[], LabelFilterMode, boolean] => {
  return [
    args.labelIds,
    args.labelFilterMode,
    args.includeZeroValueJobs,
  ] as const
}

const toQueryParams = (
  values: Record<string, number | undefined>,
): Record<string, string> => {
  const params: Record<string, string> = {}
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) {
      params[key] = String(value)
    }
  }
  return params
}

const toPeriodParams = ({ month }: AchievementPeriod): Record<string, string> =>
  toQueryParams({ month: month ?? undefined })

/**
 * Dropped as soon as they're stale, so that coming back to the page shows
 * skeletons and not old numbers while the new ones load.
 */
const ACHIEVEMENTS_CACHE_TIMES = {
  staleTime: 5 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
}
const KEY_FIGURES_CACHE_TIMES = { staleTime: 60 * 1000, gcTime: 60 * 1000 }

export default {
  // Combined trends endpoint (optimized - single request for all trend data)
  allTrends: (
    storeId: string,
    timeUnit: TimeUnit,
    spanParams: Record<string, string>,
    endDate: Date,
    filter: StatsFilterArgs,
  ) =>
    queryOptions({
      queryKey: [
        "stats",
        "trends",
        "all",
        storeId,
        timeUnit,
        spanParams,
        endDate.toISOString(),
        ...statsFilterKeyFragment(filter),
      ],
      queryFn: async () => {
        return request<AllTrendsResponse>({
          url: populateRoute(
            Route.TRENDS__ALL,
            storeId,
            timeUnit,
            endDate.toISOString(),
          ),
          params: {
            ...buildStatsFilterParams(filter),
            ...spanParams,
          },
          method: "GET",
        })
      },
      ...KEY_FIGURES_CACHE_TIMES,
    }),
  // Combined stats endpoint (optimized - single request for all stats data)
  allStats: (
    storeId: string,
    startDate: Date,
    endDate: Date,
    filter: StatsFilterArgs,
  ) =>
    queryOptions({
      queryKey: [
        "stats",
        "all",
        storeId,
        startDate.toISOString(),
        endDate.toISOString(),
        ...statsFilterKeyFragment(filter),
      ],
      queryFn: async () => {
        return request<AllStatsResponse>({
          url: populateRoute(
            Route.STATS__ALL,
            storeId,
            startDate.toISOString(),
            endDate.toISOString(),
          ),
          params: buildStatsFilterParams(filter),
          method: "GET",
        })
      },
      ...KEY_FIGURES_CACHE_TIMES,
    }),
  leadTimeStandings: (
    storeId: string,
    startDate: Date,
    endDate: Date,
    filter: StatsFilterArgs,
  ) =>
    queryOptions({
      queryKey: [
        "stats",
        "leadTimeStandings",
        storeId,
        startDate.toISOString(),
        endDate.toISOString(),
        ...statsFilterKeyFragment(filter),
      ],
      queryFn: async () => {
        return request<LeadTimeStandings>({
          url: populateRoute(
            Route.STATS__LEAD_TIME_STANDINGS,
            storeId,
            startDate.toISOString(),
            endDate.toISOString(),
          ),
          params: buildStatsFilterParams(filter),
          method: "GET",
        })
      },
      ...KEY_FIGURES_CACHE_TIMES,
    }),
  addonCard: (
    storeId: string,
    startDate: Date,
    endDate: Date,
    filter: StatsFilterArgs,
    source: AddonSource,
    cardFilter: { toggledActionKeys: string[] },
  ) =>
    queryOptions({
      queryKey: [
        "stats",
        "addon-card",
        storeId,
        startDate.toISOString(),
        endDate.toISOString(),
        ...statsFilterKeyFragment(filter),
        source,
        ...cardFilter.toggledActionKeys,
      ],
      queryFn: () =>
        request<AddonCardStats>({
          url: populateRoute(
            Route.STATS__ADDON_CARD,
            storeId,
            startDate.toISOString(),
            endDate.toISOString(),
          ),
          params: {
            ...buildStatsFilterParams(filter),
            source,
            toggledActionKeys: JSON.stringify(cardFilter.toggledActionKeys),
          },
          method: "GET",
        }),
    }),
  achievements: (storeId: string, period: AchievementPeriod) =>
    queryOptions({
      queryKey: ["stats", "achievements", storeId, period],
      queryFn: () =>
        request<AchievementsResponse>({
          url: populateRoute(
            Route.STATS__ACHIEVEMENTS,
            storeId,
            period.year.toString(),
          ),
          params: toPeriodParams(period),
          method: "GET",
        }),
      ...ACHIEVEMENTS_CACHE_TIMES,
    }),
  achievementReviews: (
    storeId: string,
    period: AchievementPeriod,
    mechanicId: string | null,
  ) =>
    queryOptions({
      queryKey: [
        "stats",
        "achievements",
        storeId,
        period,
        "reviews",
        mechanicId,
      ],
      queryFn: () =>
        request<AchievementReview[]>({
          url: populateRoute(
            Route.STATS__ACHIEVEMENT_REVIEWS,
            storeId,
            period.year.toString(),
          ),
          params: {
            ...toPeriodParams(period),
            ...(mechanicId ? { mechanicId } : {}),
          },
          method: "GET",
        }),
      ...ACHIEVEMENTS_CACHE_TIMES,
    }),
  mechanicAchievements: (
    storeId: string,
    period: AchievementPeriod,
    mechanicId: string,
  ) =>
    queryOptions({
      queryKey: ["stats", "achievements", storeId, period, mechanicId],
      queryFn: () =>
        request<MechanicAchievementsResponse>({
          url: populateRoute(
            Route.STATS__MECHANIC_ACHIEVEMENTS,
            storeId,
            period.year.toString(),
            mechanicId,
          ),
          params: toPeriodParams(period),
          method: "GET",
        }),
      ...ACHIEVEMENTS_CACHE_TIMES,
    }),
  bikeStoreCapacity: (storeId: string) =>
    queryOptions({
      queryKey: ["stats", "bike-store-capacity", storeId],
      queryFn: async () => {
        return request<BikeStoreCapacityResponse>({
          url: populateRoute(Route.STATS__BIKE_STORE_CAPACITY, storeId),
          method: "GET",
        })
      },
    }),
  priceSimulation: (storeId: string, overrides: PriceSimulationOverrides) =>
    queryOptions({
      queryKey: ["stats", "price-simulation", storeId, overrides],
      queryFn: async () => {
        return request<PriceSimulationResponse>({
          url: populateRoute(Route.STATS__PRICE_SIMULATION, storeId),
          params: toQueryParams(overrides),
          method: "GET",
        })
      },
      placeholderData: keepPreviousData,
    }),
  inspectionSimulation: (
    storeId: string,
    overrides: InspectionSimulationOverrides,
  ) =>
    queryOptions({
      queryKey: ["stats", "inspection-simulation", storeId, overrides],
      queryFn: async () => {
        return request<InspectionSimulationResponse>({
          url: populateRoute(Route.STATS__INSPECTION_SIMULATION, storeId),
          params: toQueryParams(overrides),
          method: "GET",
        })
      },
      placeholderData: keepPreviousData,
    }),
  overviewLateCounts: (storeId: string) =>
    queryOptions({
      queryKey: ["stats", "overview-late-counts", storeId],
      queryFn: async () => {
        return request<OverviewLateCounts>({
          url: populateRoute(Route.STATS__OVERVIEW_LATE_COUNTS, storeId),
          method: "GET",
        })
      },
      staleTime: 5000,
    }),
}
